import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { canShareRelease, getStatusLabel } from '../../constants/forms';
import { handleImageError } from '../../utils/imageUtils';
import { coverGradientFor } from '../../utils/coverGradient';
import PremiumGate from '../shared/PremiumGate';
import 'react-datepicker/dist/react-datepicker.css';

const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const AUDIO_ACCEPT = '.mp3,.wav,.flac,.ogg,.aac,.m4a,.wma,.opus,.alac,.aiff,audio/mpeg,audio/wav,audio/flac,audio/ogg,audio/aac,audio/mp4,audio/x-m4a,audio/opus';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const units = ['o', 'Ko', 'Mo', 'Go'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const ReleaseModal = ({
  show,
  onClose,
  releaseMode,
  releaseForm,
  releaseError,
  releaseSaving,
  handleReleaseFieldChange,
  handleCoverChange,
  handleTrackChange,
  handleSaveRelease,
  addTrackRow,
  removeTrackRow,
  handleShare,
  data,
  shareMessage,
  dragState,
  coverRatioWarning,
  handleDropAudioFiles,
  quota,
}) => {
  const trackInputRefs = useRef([]);

  const [draggedIndex, setDraggedIndex] = React.useState(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [localShareMessage, setLocalShareMessage] = React.useState('');
  const dropZoneRef = useRef(null);

  const onShare = async () => {
    if (handleShare) {
      handleShare(releaseForm);
    } else {
      const artistSlug = data?.artist?.slug;
      const albumSlug = releaseForm?.slug;
      if (!artistSlug || !albumSlug) {
        setLocalShareMessage('Impossible de générer le lien - informations manquantes');
        setTimeout(() => setLocalShareMessage(''), 3000);
        return;
      }
      const baseUrl = releaseForm?.is_free ? 'https://player.passiio.shop' : 'https://passiio.shop';
      const shareUrl = `${baseUrl}/p/${artistSlug}/${albumSlug}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        setLocalShareMessage('Lien copié !');
        setTimeout(() => setLocalShareMessage(''), 3000);
      } catch {
        setLocalShareMessage('Erreur lors de la copie');
        setTimeout(() => setLocalShareMessage(''), 3000);
      }
    }
  };

  const handleMoveTrack = (fromIndex, toIndex) => {
    if (draggedIndex === null || draggedIndex === toIndex) return;
    if (dragState?.moveTrack) {
      dragState.moveTrack(fromIndex, toIndex);
    }
    setDraggedIndex(toIndex);
  };

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Drag & Drop handlers for audio files
  const handleDragOver = (e) => {
    if (releaseMode === 'view') return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (releaseMode === 'view') return;
    e.preventDefault();
    e.stopPropagation();
    // Only set false if we actually left the drop zone (not a child)
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    if (releaseMode === 'view') return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer?.files;
    if (files?.length > 0 && handleDropAudioFiles) {
      handleDropAudioFiles(files);
    }
  };

  useEffect(() => {
    if (show && releaseForm.tracks.length > 0) {
      const lastIndex = releaseForm.tracks.length - 1;
      if (trackInputRefs.current[lastIndex]) {
        trackInputRefs.current[lastIndex].focus();
      }
    }
  }, [releaseForm.tracks.length, show]);

  // ── Quota : combien de tracks l'artiste peut-il encore ajouter dans ce formulaire ? ──
  // On compte TOUTES les pistes du formulaire (même celles sans fichier), car le backend
  // les comptabilise toutes à la création.
  const tracksInForm = releaseForm.tracks.length;
  const tracksRemaining = quota?.tracks_remaining ?? Infinity;
  const canAddMoreTracks = releaseForm.type === 'album'
    && tracksInForm < tracksRemaining
    && tracksRemaining > 0;
  const isFreePlan = data?.artist?.subscription_tier !== 'premium';
  // Même fallback cover que les listes (dégradé déterministe + initiale)
  const coverSrc = releaseForm.cover_preview || releaseForm.cover_url || null;

  /* ── Wizard mobile : création/édition en étapes (desktop inchangé) ── */
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
  );
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (show) {
      setStepIdx(0);
    }
  }, [show, releaseMode]);

  const wizardSteps = releaseMode === 'edit'
    ? [
        { id: 'infos', label: 'Infos' },
        { id: 'prix', label: 'Prix' },
        { id: 'publication', label: 'Publication' },
      ]
    : [
        { id: 'infos', label: 'Infos' },
        { id: 'prix', label: 'Prix' },
        { id: 'audio', label: 'Audio' },
        { id: 'publication', label: 'Publication' },
      ];

  const wizardActive = isMobile;
  const safeIdx = Math.min(stepIdx, wizardSteps.length - 1);
  const stepId = wizardSteps[safeIdx].id;
  const isLastStep = safeIdx >= wizardSteps.length - 1;

  const hasCover = !!(releaseForm.cover_file || releaseForm.cover_url || releaseForm.cover_preview);
  const titleOk = (releaseForm.title || '').trim() !== '';
  const coverOk = releaseMode === 'edit' || hasCover;
  const priceOk = releaseForm.is_free || Number(releaseForm.price_ariary) >= 1000;
  const tracksOk = releaseForm.tracks.length > 0
    && releaseForm.tracks.every((t) => t.file || t.encrypted_audio_url);
  const publiOk = releaseForm.status !== 'scheduled' || !!releaseForm.publication_date;

  const stepValid = {
    infos: titleOk && coverOk,
    prix: priceOk,
    audio: tracksOk,
    publication: publiOk,
  };
  const stepMissing = {
    infos: [!titleOk && 'Ajoute un titre pour continuer', !coverOk && 'Ajoute une pochette pour continuer'].filter(Boolean),
    prix: [!priceOk && 'Prix minimum : 1 000 Ar'].filter(Boolean),
    audio: [
      releaseForm.tracks.length === 0 && 'Ajoute au moins une piste pour continuer',
      releaseForm.tracks.length > 0 && !tracksOk && 'Chaque piste doit avoir son fichier audio',
    ].filter(Boolean),
    publication: [!publiOk && 'Choisis une date de publication pour continuer'].filter(Boolean),
  };

  const canNext = !!stepValid[stepId];
  const goNext = () => {
    if (!canNext || isLastStep) return;
    setStepIdx(safeIdx + 1);
  };
  const goBack = () => setStepIdx((i) => Math.max(0, i - 1));
  const paneOn = (...ids) => !wizardActive || ids.includes(stepId);

  if (!show) return null;

  /* ── Mode détails : présentation DNA (création/édition inchangées plus bas) ── */
  if (releaseMode === 'view') {
    const tracks = releaseForm.tracks || [];
    const isFree = releaseForm.is_free || Number(releaseForm.price_ariary || 0) === 0;
    const coverSrc = releaseForm.cover_preview || releaseForm.cover_url || null;
    return (
      <div className="release-modal-backdrop" role="presentation" onClick={onClose}>
        <div className="release-modal dna-release-view" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
          <button className="dna-modal-close icon-button" onClick={onClose} aria-label="Fermer">
            <i className="fa-solid fa-xmark" />
          </button>

          {releaseError && <div className="auth-error" style={{ marginBottom: 16 }}>{releaseError}</div>}

          <div className="dna-release-hero">
            <div
              className="dna-release-cover dna-release-cover-fallback"
              style={coverSrc ? undefined : { background: coverGradientFor(releaseForm.id || releaseForm.title) }}
            >
              {coverSrc ? (
                <img src={coverSrc} alt={releaseForm.title || 'Pochette'} onError={handleImageError} />
              ) : (
                <span className="dna-release-cover-initial" aria-hidden="true">
                  {(releaseForm.title || 'P')[0].toUpperCase()}
                </span>
              )}
            </div>
            <div className="dna-release-head">
              <span className="content-kicker">{releaseForm.type === 'single' ? 'Single' : 'Album'}</span>
              <h2>{releaseForm.title || 'Sans titre'}</h2>
              <div className="dna-release-badges">
                <span className={`status status-${String(releaseForm.status || 'draft')}`}>{getStatusLabel(releaseForm.status)}</span>
                <span className="dna-price-pill">{isFree ? 'Gratuit' : `${Number(releaseForm.price_ariary || 0).toLocaleString()} Ar`}</span>
              </div>
              <p className="dna-release-desc">
                {releaseForm.description || 'Aucune description pour cette release.'}
              </p>
              <div className="dna-release-actions">
                {canShareRelease(releaseForm.status) && (
                  <button type="button" className="button button-outline" onClick={onShare}>
                    <i className="fa-solid fa-share-nodes" /> Partager le lien
                  </button>
                )}
                <button type="button" className="button button-crimson" onClick={onClose}>
                  Fermer <i className="fa-solid fa-arrow-right" style={{ fontSize: '.7rem' }} />
                </button>
              </div>
            </div>
          </div>

          <div className="dna-release-tracks">
            <div className="dna-tracks-head">
              <span className="content-kicker">Pistes</span>
              <b>{tracks.length} piste{tracks.length > 1 ? 's' : ''}</b>
            </div>
            {tracks.length === 0 ? (
              <p className="dna-tracks-empty">Aucune piste sur cette release.</p>
            ) : (
              tracks.map((track, index) => (
                <div className="dna-track-row" key={track.id || `track-${index}`}>
                  <span className="dna-track-num">{String(index + 1).padStart(2, '0')}</span>
                  <span className="dna-track-title">{track.title || `Piste ${index + 1}`}</span>
                  {track.duration && formatDuration(track.duration) !== '0:00' && (
                    <span className="dna-track-dur">{formatDuration(track.duration)}</span>
                  )}
                </div>
              ))
            )}
          </div>

          {(shareMessage || localShareMessage) && (
            <div style={{
              position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
              background: '#189b69', color: '#fff', padding: '10px 20px',
              borderRadius: '10px', zIndex: 10000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              {shareMessage || localShareMessage}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="release-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="release-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="release-modal-header">
          <div>
            <p className="release-modal-kicker">
              {releaseMode === 'create' ? 'Nouvelle release' : releaseMode === 'view' ? 'Détails de la release' : 'Modifier la release'}
            </p>
            <h3 className="release-modal-title">
              {releaseMode === 'create' ? 'Créer une release' : releaseMode === 'view' ? 'Aperçu' : 'Éditer une release'}
            </h3>
          </div>
          <button className="btn-close-popup" onClick={onClose}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {releaseError && (
          <div className="release-modal-error">{releaseError}</div>
        )}

        <form className={`release-form${wizardActive ? ` wizard-step-${stepId}` : ''}`} onSubmit={handleSaveRelease}>
          {/* Progression du wizard (mobile uniquement, masquée sur desktop en CSS) */}
          {wizardActive && (
            <div className="release-wizard-progress">
              <div className="wizard-top">
                <span>Étape {safeIdx + 1} / {wizardSteps.length}</span>
                <b>{wizardSteps[safeIdx].label}</b>
              </div>
              <div className="wizard-dots">
                {wizardSteps.map((s, i) => (
                  <span key={s.id} className={i < safeIdx ? 'done' : i === safeIdx ? 'active' : ''} />
                ))}
              </div>
            </div>
          )}
          {/* Step 1 : Cover + Titre + Format */}
          <div className={`release-modal-section release-section-main step-pane ${paneOn('infos') ? 'is-active' : ''}`}>
            <div className="release-cover-column">
              <div
                className="release-cover-preview"
                style={coverSrc ? undefined : { background: coverGradientFor(releaseForm.id || releaseForm.title) }}
              >
                {coverSrc ? (
                  <img
                    src={coverSrc}
                    alt={releaseForm.title || 'Prévisualisation'}
                    onError={handleImageError}
                  />
                ) : (
                  <span className="cover-fallback-initial" aria-hidden="true">
                    {(releaseForm.title || 'R')[0].toUpperCase()}
                  </span>
                )}
                {releaseMode !== 'view' && (
                  <div className="cover-overlay">
                    <i className="fa-solid fa-camera"></i>
                    <span>Changer</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleCoverChange(e.target.files?.[0])}
                    />
                  </div>
                )}
              </div>
              {coverRatioWarning && (
                <div className="cover-ratio-warning">
                  <i className="fa-solid fa-triangle-exclamation me-1"></i>
                  {coverRatioWarning}
                </div>
              )}
            </div>

            <div className="release-info-column">
              <label className="release-field">
                <span>Titre de la release</span>
                <input
                  type="text"
                  id="release-title"
                  name="release-title"
                  value={releaseForm.title}
                  onChange={(e) => handleReleaseFieldChange('title', e.target.value)}
                  placeholder="Ex: Moonlight Sonata"
                  required
                  disabled={releaseMode === 'view'}
                />
              </label>
              <label className="release-field">
                <span>Format / Type</span>
                <select
                  value={releaseForm.type}
                  onChange={(e) => handleReleaseFieldChange('type', e.target.value)}
                  disabled={releaseMode === 'view'}
                >
                  <option value="album">EP / Album</option>
                  <option value="single">Single</option>
                </select>
              </label>
            </div>
          </div>

          {/* Step 2 : Prix + Description */}
          <div className={`release-modal-section release-section-details release-step-prix step-pane ${paneOn('prix') ? 'is-active' : ''}`}>
            <div className="release-details-left">
              <div className="prix-block">
                <div className="prix-block-head">
                  <span className="prix-block-icon" aria-hidden="true">
                    <i className="fa-solid fa-tag"></i>
                  </span>
                  <div>
                    <b>Prix de vente</b>
                    <small>Tarif en Ariary · minimum 1 000 Ar</small>
                  </div>
                </div>
                <div className="release-field">
                  {/* Choix Payant / Gratuit : mêmes cartes que l'étape Publication */}
                  {releaseForm.type === 'single' && releaseMode !== 'view' && (
                    <div className="publication-toggle-row prix-toggle-row">
                      <button
                        type="button"
                        className={`publication-option ${!releaseForm.is_free ? 'is-active' : ''}`}
                        onClick={() => handleReleaseFieldChange('is_free', false)}
                      >
                        <i className="fa-solid fa-tag"></i>
                        <div>
                          <span className="pub-option-label">Payant</span>
                          <span className="pub-option-desc">Fixer un prix</span>
                        </div>
                      </button>
                      <PremiumGate
                        feature="is_free"
                        artist={data?.artist}
                        label="Singles gratuits réservés aux artistes Premium"
                      >
                        <button
                          type="button"
                          className={`publication-option ${releaseForm.is_free ? 'is-active' : ''}`}
                          onClick={() => handleReleaseFieldChange('is_free', true)}
                        >
                          <i className="fa-solid fa-gift"></i>
                          <div>
                            <span className="pub-option-label">Gratuit</span>
                            <span className="pub-option-desc">Offrir aux fans</span>
                          </div>
                        </button>
                      </PremiumGate>
                    </div>
                  )}
                  {!releaseForm.is_free ? (
                    <div className="price-with-currency">
                      <input
                        type="number"
                        id="release-price"
                        name="release-price"
                        min="1000"
                        value={releaseForm.price_ariary}
                        onChange={(e) => handleReleaseFieldChange('price_ariary', e.target.value)}
                        required={!releaseForm.is_free}
                        placeholder="Ex: 5000"
                        disabled={releaseMode === 'view'}
                      />
                      <span className="currency-label">Ar</span>
                      {releaseMode !== 'view' && (
                        <small className="price-min-hint">Prix minimum : 1 000 Ar</small>
                      )}
                    </div>
                  ) : (
                    <div className="free-badge free-badge-small">
                      <i className="fa-solid fa-gift"></i> Gratuit
                    </div>
                  )}
                </div>
              </div>
            </div>

            {!(releaseMode === 'view' && !releaseForm.description) && (
              <div className="release-details-right">
                <div className="prix-block">
                  <div className="prix-block-head">
                    <span className="prix-block-icon" aria-hidden="true">
                      <i className="fa-solid fa-pen-nib"></i>
                    </span>
                    <div>
                      <b>Description</b>
                      <small>Raconte l'histoire de ce projet</small>
                    </div>
                  </div>
                  <label className="release-field">
                    <textarea
                      className="prix-textarea"
                      value={releaseForm.description}
                      onChange={(e) => handleReleaseFieldChange('description', e.target.value)}
                      placeholder="Ex : Un single afro-gasy enregistré à Tana…"
                      disabled={releaseMode === 'view'}
                    />
                  </label>
                  <div className="prix-foot">
                    <small>Optionnel mais recommandé</small>
                    <small>{(releaseForm.description || '').length} caractères</small>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step 3 : Audio */}
          {(releaseMode === 'create' || releaseMode === 'view') && (
            <div
              ref={dropZoneRef}
              className={`release-section-tracks shadow-sm release-modal-section step-pane ${paneOn('audio') ? 'is-active' : ''} ${isDragOver && releaseMode !== 'view' ? 'drag-over' : ''} ${releaseMode !== 'view' ? 'is-drop-target' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Drop zone overlay */}
              {isDragOver && releaseMode !== 'view' && (
                <div className="release-tracks-dropzone">
                  <div className="dropzone-content">
                    <i className="fa-solid fa-cloud-arrow-up dropzone-icon"></i>
                    <span className="dropzone-label">Déposez vos fichiers audio</span>
                    <span className="dropzone-hint">MP3, WAV, FLAC, OGG, AAC, M4A…</span>
                  </div>
                </div>
              )}
              <div className="d-flex align-items-center justify-content-between mb-4 release-tracks-head">
                <div>
                  <h4 className="h5 fw-bold text-navy mb-1">Pistes</h4>
                  <p className="text-muted small mb-0">{releaseForm.tracks.length} piste(s) configurée(s)</p>
                  {releaseMode !== 'view' && (
                    <p className="text-muted small mb-0 mt-1 dropzone-hint-text">
                      <i className="fa-regular fa-hand-pointer me-1"></i>
                      Cliquez sur Audio ou glissez-déposez vos fichiers ici
                    </p>
                  )}
                </div>
                {releaseForm.type === 'album' && releaseMode !== 'view' && (
                  <>
                    {canAddMoreTracks ? (
                      <button type="button" className="btn btn-outline-navy fw-bold rounded-pill btn-add-track-custom" onClick={addTrackRow}>
                        <i className="fa-solid fa-plus me-1"></i>
                        Ajouter une piste
                      </button>
                    ) : (
                      <span className="quota-limit-badge">
                        <i className="fa-solid fa-circle-info me-1"></i>
                        {isFreePlan
                          ? `Limite de ${quota?.free_track_limit || 3} tracks atteinte — Passez Premium pour plus`
                          : `Limite de ${quota?.premium_track_limit || 30} tracks atteinte`}
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="release-tracks-list">
                {releaseForm.tracks.map((track, index) => (
                  <div
                    className={`release-track-item ${draggedIndex === index ? 'is-dragging' : ''} ${releaseMode === 'view' ? 'view-mode' : ''} border-0 bg-light-subtle mb-2`}
                    key={track.id || `track-${index}`}
                    draggable={releaseForm.type === 'album' && releaseMode !== 'view'}
                    onDragStart={releaseMode !== 'view' ? () => handleDragStart(index) : undefined}
                    onDragOver={releaseMode !== 'view' ? (e) => { e.preventDefault(); handleMoveTrack(draggedIndex, index); } : undefined}
                    onDragEnd={releaseMode !== 'view' ? handleDragEnd : undefined}
                  >
                    {releaseMode !== 'view' && (
                      <div className="track-drag-handle text-muted">
                        <i className="fa-solid fa-grip-vertical"></i>
                      </div>
                    )}

                    <div className="track-number fw-bold text-primary">{String(index + 1).padStart(2, '0')}</div>

                    <div className="track-content">
                      <div className="track-main-info">
                        <input
                          ref={(el) => (trackInputRefs.current[index] = el)}
                          type="text"
                          id={`track-title-${index}`}
                          name={`track-title-${index}`}
                          className={`track-title-input border-0 bg-transparent fw-bold ${releaseForm.type === 'single' ? 'is-locked' : ''}`}
                          value={track.title}
                          onChange={(e) => handleTrackChange(index, 'title', e.target.value)}
                          placeholder="Titre de la piste"
                          readOnly={releaseForm.type === 'single' || releaseMode === 'view'}
                        />
                        <div className="track-meta d-flex align-items-center gap-3">
                          {track.duration && formatDuration(track.duration) !== '0:00' && (
                            <span className="badge bg-navy-subtle text-navy font-monospace small">
                              <i className="fa-regular fa-clock me-1"></i>
                              {formatDuration(track.duration)}
                            </span>
                          )}
                          {releaseForm.type === 'single' && releaseMode !== 'view' && (
                            <span className="text-primary small fw-semibold">
                              <i className="fa-solid fa-link me-1"></i>
                              Identique au titre
                            </span>
                          )}
                        </div>
                      </div>

                      {releaseMode !== 'view' && (
                        <div className="track-upload-group d-flex gap-2">
                          <label className={`btn btn-sm ${track.file ? 'btn-success' : 'btn-light'} rounded-pill flex-fill transition-all d-flex align-items-center justify-content-center gap-1 position-relative`} title={track.file ? track.file.name : 'Ajouter un fichier audio'}>
                            {track.file ? (
                              <>
                                <i className="fa-solid fa-check"></i>
                                <span className="fw-bold track-file-name">{track.file.name.replace(/\.[^.]+$/, '')}</span>
                                <span className="track-file-ext">{track.file.name.split('.').pop()?.toLowerCase()}</span>
                                {track.file.size > 0 && (
                                  <span className="track-file-size">{formatFileSize(track.file.size)}</span>
                                )}
                              </>
                            ) : (
                              <>
                                <i className="fa-solid fa-cloud-arrow-up"></i>
                                <span className="fw-bold">Audio</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept={AUDIO_ACCEPT}
                              onChange={(e) => handleTrackChange(index, 'file', e.target.files?.[0] || null)}
                              hidden
                            />
                          </label>
                          <PremiumGate
                            feature="lrc"
                            artist={data?.artist}
                            label="Paroles synchronisées LRC réservées aux artistes Premium"
                          >
                            <label className={`btn btn-sm ${track.lyrics_file || track.has_lyrics ? 'btn-info text-white' : 'btn-light'} rounded-pill flex-fill transition-all d-flex align-items-center justify-content-center`} title="Fichier LRC (paroles)">
                              <i className="fa-solid fa-closed-captioning me-1"></i>
                              <span className="fw-bold">
                                {track.lyrics_file || track.has_lyrics ? 'LRC ✓' : 'LRC'}
                              </span>
                              <input
                                type="file"
                                accept=".lrc,text/plain"
                                onChange={(e) => handleTrackChange(index, 'lyrics_file', e.target.files?.[0] || null)}
                                hidden
                              />
                            </label>
                          </PremiumGate>
                        </div>
                      )}
                    </div>

                    {releaseMode !== 'view' && (
                      <div className="track-actions align-self-start pt-1">
                        <button
                          type="button"
                          className="btn btn-link text-danger p-1"
                          onClick={() => removeTrackRow(index)}
                          title="Supprimer la piste"
                        >
                          <i className="fa-solid fa-trash-can"></i>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4 : Publier ou Programmer */}
          <div className={`release-modal-section release-section-publication step-pane ${paneOn('publication') ? 'is-active' : ''}`}>
            <div className="publication-card publication-card-standalone">
              <div className="publication-card-header">
                <div className="publication-card-icon">
                  <i className="fa-solid fa-upload"></i>
                </div>
                <div>
                  <span className="publication-card-title">Publication</span>
                  <span className="publication-card-subtitle">Choisissez quand mettre en ligne</span>
                </div>
              </div>

              <div className="publication-toggle-row">
                <button
                  type="button"
                  className={`publication-option ${releaseForm.status === 'published' ? 'is-active' : ''}`}
                  onClick={() => handleReleaseFieldChange('status', 'published')}
                  disabled={releaseMode === 'view'}
                >
                  <i className="fa-solid fa-globe"></i>
                  <div>
                    <span className="pub-option-label">Publié</span>
                    <span className="pub-option-desc">Disponible immédiatement</span>
                  </div>
                </button>

                <button
                  type="button"
                  className={`publication-option ${releaseForm.status === 'scheduled' ? 'is-active' : ''}`}
                  onClick={() => handleReleaseFieldChange('status', 'scheduled')}
                  disabled={releaseMode === 'view'}
                >
                  <i className="fa-solid fa-calendar-clock"></i>
                  <div>
                    <span className="pub-option-label">Programmé</span>
                    <span className="pub-option-desc">Planifier une date</span>
                  </div>
                </button>
              </div>

              {releaseForm.status === 'scheduled' && (
                <div className="scheduled-section">
                  <div className="scheduled-date-row">
                    <DatePicker
                      selected={releaseForm.publication_date ? new Date(releaseForm.publication_date) : new Date()}
                      onChange={(date) => handleReleaseFieldChange('publication_date', date ? date.toISOString() : new Date().toISOString())}
                      dateFormat="dd/MM/yyyy"
                      locale={fr}
                      disabled={releaseMode === 'view'}
                      className="release-date-picker"
                      placeholderText="Sélectionner une date"
                    />
                    <i className="fa-solid fa-calendar date-input-icon"></i>
                  </div>

                  {releaseForm.publication_date && (
                    <div className="scheduled-date-preview">
                      {(() => {
                        const selectedDate = new Date(releaseForm.publication_date);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        selectedDate.setHours(0, 0, 0, 0);
                        const diffDays = Math.ceil((selectedDate - today) / (1000 * 60 * 60 * 24));
                        const isToday = diffDays === 0;
                        const isTomorrow = diffDays === 1;
                        const isPast = diffDays < 0;

                        return (
                          <>
                            <div className={`scheduled-date-badge ${isToday ? 'is-today' : isPast ? 'is-past' : ''}`}>
                              <i className={`fa-solid ${isToday ? 'fa-sun' : isPast ? 'fa-clock-rotate-left' : 'fa-calendar-day'}`}></i>
                              <span>
                                {isToday ? "Aujourd'hui" : isTomorrow ? 'Demain' : format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })}
                              </span>
                            </div>
                            {!isToday && !isPast && (
                              <span className="scheduled-countdown">
                                <i className="fa-regular fa-clock"></i>
                                Dans {diffDays} jour{diffDays > 1 ? 's' : ''}
                              </span>
                            )}
                            {isPast && (
                              <span className="scheduled-countdown is-overdue">
                                <i className="fa-solid fa-triangle-exclamation"></i>
                                Cette date est déjà passée
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {releaseMode === 'edit' && (
            <p className={`release-hint step-pane ${paneOn('publication') ? 'is-active' : ''}`}>
              Pour l'instant, l'édition met à jour la fiche release et la pochette. Les pistes peuvent être
              recréées via le flux d'upload Telegram si besoin.
            </p>
          )}

          <div className="release-actions">
            {releaseMode !== 'view' ? (
              <>
                <button type="button" className="btn-release-secondary" onClick={onClose}>
                  Annuler
                </button>
                <button type="submit" className="btn-release-primary" disabled={releaseSaving}>
                  {releaseSaving ? 'Sauvegarde...' : releaseMode === 'create' ? 'Créer la release' : 'Enregistrer'}
                </button>
              </>
            ) : (
              <>
                {canShareRelease(releaseForm.status) && (
                  <button
                    type="button"
                    className="btn-release-share"
                    onClick={onShare}
                    style={{
                      background: 'transparent',
                      border: '1px solid #6c757d',
                      color: '#6c757d',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <i className="fa-solid fa-share-nodes"></i>
                    Partager le lien
                  </button>
                )}
                <button type="button" className="btn-release-primary" onClick={onClose}>
                  Fermer
                </button>
              </>
            )}
          </div>

          {/* Pied du wizard (mobile uniquement, masqué sur desktop en CSS) */}
          {wizardActive && (
            <div className="release-wizard-footer">
              {safeIdx > 0 ? (
                <button type="button" className="btn-release-secondary" onClick={goBack}>
                  Retour
                </button>
              ) : (
                <button type="button" className="btn-release-secondary" onClick={onClose}>
                  Annuler
                </button>
              )}
              {!isLastStep ? (
                <button type="button" className="btn-release-primary" onClick={goNext} disabled={!canNext}>
                  Suivant
                </button>
              ) : (
                <button type="submit" className="btn-release-primary" disabled={releaseSaving}>
                  {releaseSaving ? 'Sauvegarde...' : releaseMode === 'create' ? 'Créer la release' : 'Enregistrer'}
                </button>
              )}
              {!isLastStep && !canNext && stepMissing[stepId].length > 0 && (
                <p className="wizard-hint">{stepMissing[stepId][0]}</p>
              )}
            </div>
          )}
        </form>

        {(shareMessage || localShareMessage) && (
          <div className="share-toast-modal" style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#28a745',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            zIndex: 10000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            {shareMessage || localShareMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReleaseModal;
